import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, FileText, Download, Eye, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { authApi, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function DataRoom() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => { document.title = "Investor Data Room | norse3"; }, []);
  useEffect(() => {
    (async () => {
      try { const r = await authApi.get("/portal/documents"); setDocs(r.data); }
      catch (e) { setDenied(true); }
    })();
  }, []);

  const openDoc = async (id) => {
    try {
      const r = await authApi.get(`/portal/documents/${id}/link`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`, "_blank", "noopener");
    } catch (e) { toast.error("Unable to open document."); }
  };

  return (
    <div className="min-h-screen pt-16 bg-navy-950">
      <div className="mx-auto max-w-6xl px-5 lg:px-8 py-10">
        <Link to="/investor/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to dashboard</Link>
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-primary">Secure data room</p>
            <h1 className="mt-1 font-heading text-2xl font-bold text-foreground">Investor materials</h1>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs text-primary"><ShieldCheck className="h-3.5 w-3.5" /> Private &amp; Confidential</span>
        </div>

        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">Documents open via short-lived secure links and are watermarked with your name, email, reference and access time. Do not forward, reproduce or share them.</p>

        {denied && (
          <div className="mt-8 rounded-lg border border-signal-red/30 bg-signal-red/[0.06] p-8 text-center">
            <Lock className="mx-auto h-8 w-8 text-signal-red" />
            <p className="mt-3 text-foreground font-medium">Access not granted</p>
            <p className="mt-1 text-sm text-muted-foreground">Access to the restricted investor materials has not been granted, or has expired or been revoked.</p>
            <Button asChild className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/investor/dashboard">Return to dashboard</Link></Button>
          </div>
        )}

        {!denied && !docs && <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 text-primary animate-spin" /></div>}

        {!denied && docs && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {docs.map((d) => (
              <div key={d.id} className={`rounded-xl border p-5 ${d.available ? "border-white/10 bg-navy-900/60" : "border-white/10 bg-white/[0.02] opacity-60"}`}>
                <div className="flex items-center justify-between">
                  <FileText className="h-6 w-6 text-primary" />
                  {!d.available && <Lock className="h-4 w-4 text-muted-foreground" />}
                </div>
                <h3 className="mt-3 text-base font-semibold text-foreground">{d.title}</h3>
                <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                  <p>Version: {d.version}</p>
                  <p>Updated: {d.updated_at}</p>
                  <p>{d.confidential}</p>
                </div>
                {d.available ? (
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" onClick={() => openDoc(d.id)} className="bg-primary text-primary-foreground hover:bg-primary/90"><Eye className="mr-1 h-3.5 w-3.5" /> View</Button>
                    {d.download && <Button size="sm" variant="outline" onClick={() => openDoc(d.id)} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Download className="mr-1 h-3.5 w-3.5" /> Download</Button>}
                  </div>
                ) : <p className="mt-4 text-xs text-muted-foreground">Restricted for your access level.</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
